// TODO: most of these selectros are actually workshop selectors, they should be moved to workshopSelectors.js

import { getGameInfoById } from 'lib/utilityFunctions';
import { createSelector } from 'reselect';
import BookList from 'a/hub/data/BookList';
import { ACTIVITY_STATUS, VOCAB_PROGRESS, WORKSHOP_BENCHMARK_IDS } from 'root/activities/hub/data/constants';
import {
  getIsChallengeQuest,
  getChallengeQuestGamesCompleteCount,
  getCurrentWorkshopQuest,
  getWorkshopBenchmark,
  getMasteredGamesCount,
  getTotalQuestGamesCount,
  getCurrentStepConfig,
  getGamesForDisplay,
} from './workshopSelectors';
import { getIndexFromActivityId } from '../lib/workshopUtility';

export const getStepFailures = ({ questWorkflowReducer: { stepFailures } }) => stepFailures;

export const getCloseReadingTitle = ({
  tfeReducer: { config }, workshopReducer: { workshop, currentQuestId, lastQuestFinishedBeforeEndOfQuests },
}) => {
  let questId = currentQuestId;
  if (questId === 0 && lastQuestFinishedBeforeEndOfQuests) {
    questId = lastQuestFinishedBeforeEndOfQuests;
  }
  const episode = workshop[`quest${questId}`]?.TFE.episode.id || '';
  const { name = '' } = config.find(({ id }) => id === episode) || {};
  return name;
};

/**
 * Returns the title and current chapter of the book assigned to the current quest.
 * Unlike `getEReaderDetails`, the title isn't truncated, and it reads only the current
 * quest - there's no fallback to the previous quest's book.
 *
 * Books without chapters return an empty `currentChapter`, and an unknown or missing
 * book returns an empty `title`.
 * @returns {{ title: string, currentChapter: string }}
 */
export const getCurrentBookDetails = createSelector(
  [getCurrentWorkshopQuest],
  ({ eReader }) => {
    const { bookId, chapter } = eReader || {};
    const currentBook = BookList.find(({ canonicalName }) => canonicalName === bookId) || {};
    return {
      title: currentBook.displayName || '',
      currentChapter: chapter || '',
    };
  },
);

/**
 * Returns an object of required details for the eReader
 * section of the Student Progress screen after extracting the data from
 * the store. See file comment at the top for more context on behavior.
 *
 * Books without chapters return `hasChapters: false` and no tabs, so consumers
 * render the book summary instead of chapter bookmarks.
 * @param {object} state the entire state from store, which will be destructured
 * @returns {object} an object of shape:
 *   {
 *     title: string,
 *     copy: string,
 *     hasChapters: boolean,
 *     tabs: { copy?: string }[],
 *   }
 * Chapters the student hasn't reached yet are empty objects, so `copy` is absent
 * for them - consumers use that to render the bookmark as unavailable.
 */
export const getEReaderDetails = ({
  workshopReducer: { currentQuestId, workshop, lastQuestFinishedBeforeEndOfQuests },
}) => {
  // To retrieve the right data, the progress in both the current and
  // previous quest must be compared
  let bookId;
  let chapter;

  if (currentQuestId === 0 && lastQuestFinishedBeforeEndOfQuests) {
    const eReaderData = workshop[`quest${lastQuestFinishedBeforeEndOfQuests}`].eReader;
    bookId = eReaderData.bookId;
    chapter = eReaderData.chapter;
  } else {
    const currentQuest = workshop[`quest${currentQuestId}`];
    const previousQuest = workshop[`quest${currentQuest.previousQuestId}`] || currentQuest;
    if (currentQuest.eReader.status === ACTIVITY_STATUS.INACTIVE
      || currentQuest.eReader.status === ACTIVITY_STATUS.CALL_TO_ACTION) {
      bookId = previousQuest.eReader.bookId;
      chapter = previousQuest.eReader.chapter;
    } else {
      bookId = currentQuest.eReader.bookId;
      chapter = currentQuest.eReader.chapter;
    }
  }
  const { displayName, chapterNames, chapterSummaries, summary } = BookList
    .find(({ canonicalName }) => canonicalName === bookId)
    || { displayName: '', chapterNames: [], chapterSummaries: [], summary: '' };
  const hasChapters = chapterSummaries.length > 0;
  const index = chapterNames.findIndex(name => name === chapter);
  const tabs = chapterSummaries.map((chapterSummary, i) => (i <= index ? { copy: chapterSummary } : {}));
  // There's a text truncation problem with long eReader display names. text-overflow
  // and numberOfLines don't work in our current project config. We'd need latest React Native Web
  // for that, so this is a very simple (and therefore not foolproof) version for our
  // uses on the student progress screen.
  const titleArray = displayName.split(' ');
  const title = titleArray.length > 5
    ? titleArray.slice(0, 5).join(' ').concat('...')
    : titleArray.join(' ');
  return ({
    title, // As seen in Section Title Card
    hasChapters,
    copy: (hasChapters ? chapterSummaries[0] : summary) || '', // Default Section Info text
    tabs, // Used for navigating sub-section of Section Info; empty for books without chapters
  });
};

/**
 * Returns an object of required details for the Vocab App
 */
const getVocabAppDetailsForCurrentQuest = ({
  workshopReducer: {
    currentQuestId,
    lastQuestFinishedBeforeEndOfQuests,
    vocabResults,
  },
}) => {
  const currentQuest = currentQuestId || lastQuestFinishedBeforeEndOfQuests;
  const vocabData = vocabResults[currentQuest] || { completed: 0, recentWords: [] };
  return {
    recentWords: vocabData.recentWords.join(', '),
    completed: vocabData.completed,
  };
};

/* Vocab progress by recent words given by Vocab app */
export const getRecentWordsOfVocabProgress = createSelector(
  [getVocabAppDetailsForCurrentQuest],
  ({ recentWords }) => recentWords);

export const getVocabSessionsCompleteCount = createSelector(
  [getCurrentWorkshopQuest],
  ({ vocab }) => vocab.sessions.filter(session => session.progress === VOCAB_PROGRESS.COMPLETE).length,
);

export const getCurrentVocabSessionId = createSelector(
  [getCurrentWorkshopQuest],
  ({ vocab }) => {
    let sessionId = 0;
    if (vocab.sessions) {
      for (let index = 0; index < 3; index++) {
        if (vocab.sessions[index].progress !== VOCAB_PROGRESS.COMPLETE) {
          sessionId = vocab.sessions[index].id;
          break;
        }
      }
      return sessionId;
    }
    return 0;
  },
);

const getStudentProgressState = state => state.studentProgress;

export const getEndOfLevelSummary = createSelector(
  [getStudentProgressState],
  ({ lastLevelSummary }) => lastLevelSummary);

export const getShouldShowEndOfLevelSummary = createSelector(
  [getEndOfLevelSummary],
  ({ showSummaryData }) => !!showSummaryData,
);

export const getLastAppLaunched = createSelector(
  [getStudentProgressState],
  ({ lastAppLaunched }) => lastAppLaunched);

export const getCurrentQuestGamesInfo = createSelector(
  [getCurrentWorkshopQuest],
  ({ games }) => {
    const currentGames = games.map(({ game, skillId, levelsMastered, levels }) => {
      const { canonicalName, displayName } = getGameInfoById(game);
      return {
        levelsMastered,
        levels,
        canonicalName,
        displayName,
        skillId,
        completed: levelsMastered.length >= levels.length,
        isStarted: levelsMastered.length !== 0 && levelsMastered.length < levels.length,
        percentComplete: levelsMastered.length / levels.length,
      };
    });
    return currentGames;
  });

export const getFirstAvailableGameToPlayIndex = createSelector(
  [getCurrentQuestGamesInfo],
  (games) => {
    let firstSelectedGameIndex = games.findIndex(game => !game.completed);
    if (firstSelectedGameIndex === -1) {
      firstSelectedGameIndex = 0;
    }
    return firstSelectedGameIndex;
  });

export const getLevelFailureCount = (game, stepFailures, level) => {
  const failedCount = stepFailures[`${game}_${level}`] || 0;
  return failedCount;
};

export const getIsTFEComplete = createSelector(
  [
    getCurrentWorkshopQuest,
    getIsChallengeQuest,
  ],
  ({ TFE: { episode } }, isChallengeQuest) => {
    const isComplete = episode.progress === 'complete';
    return !isChallengeQuest && isComplete;
  },
);

export const getIsTFEPartComplete = createSelector(
  [
    getCurrentWorkshopQuest,
    getIsChallengeQuest,
    (_, activityId) => activityId,
  ],
  ({ TFE: { episode: { parts } } }, isChallengeQuest, activityId) => {
    const index = getIndexFromActivityId(activityId);
    const isComplete = parts[index].progress === 'complete';
    return !isChallengeQuest && isComplete;
  },
);

export const getCloseReadingProgressText = createSelector(
  [
    getCurrentWorkshopQuest,
    getWorkshopBenchmark,
  ],
  ({ TFE }, benchmark) => {
    const isWellBelow = benchmark === WORKSHOP_BENCHMARK_IDS.WELL_BELOW;
    if (isWellBelow) {
      const total = TFE.episode.parts.length;
      if (TFE.episode.progress === 'complete') {
        return `${total}/${total}`;
      }
      const complete = TFE.episode.parts.filter(part => part.progress === 'complete').length;
      return `${complete}/${total}`;
    }
    return TFE.episode.progress === 'complete' ? '1/1' : '0/1';
  },
);

export const getCloseReadingProgressRatio = createSelector(
  [
    getCurrentWorkshopQuest,
    getWorkshopBenchmark,
  ],
  ({ TFE }, benchmark) => {
    const isWellBelow = benchmark === WORKSHOP_BENCHMARK_IDS.WELL_BELOW;
    if (isWellBelow) {
      if (TFE.episode.progress === 'complete') {
        return 1;
      }
      const complete = TFE.episode.parts.filter(part => part.progress === 'complete').length;
      const total = TFE.episode.parts.length;
      return complete / total;
    }
    return TFE.episode.progress === 'complete' ? 1 : 0;
  },
);

export const getIsEreaderComplete = createSelector(
  [
    getCurrentWorkshopQuest,
    getIsChallengeQuest,
  ],
  ({ eReader: { progress } }) => {
    const isComplete = progress === 'complete';
    return isComplete;
  },
);

export const getEreaderProgressText = createSelector(
  [getIsEreaderComplete],
  (isComplete) => {
    if (isComplete) {
      return '1/1';
    }
    return '0/1';
  },
);

export const getEreaderProgressRatio = createSelector(
  [
    getCurrentWorkshopQuest,
    getIsEreaderComplete,
  ],
  ({ eReader: { status, progress } }, isComplete) => {
    if (isComplete) {
      return 1;
    }
    switch (status) {
      default:
      case ACTIVITY_STATUS.INACTIVE:
        return 0;
      case ACTIVITY_STATUS.CALL_TO_ACTION:
      case ACTIVITY_STATUS.IDLE:
        if (progress) {
          return 0.5;
        }
        return 0;
      case ACTIVITY_STATUS.REWARD:
      case ACTIVITY_STATUS.FINISHED:
      case ACTIVITY_STATUS.FINISHED_CALL_TO_ACTION:
        return 1;
    }
  },
);

export const getIsVocabComplete = createSelector(
  [getCurrentWorkshopQuest],
  (status) => (status === ACTIVITY_STATUS.FINISHED),
);

// Vocab / Radio : number of vocabulary activities complete (not mastered) / total number of activities available
export const getVocabProgressText = createSelector(
  [getVocabSessionsCompleteCount],
  completeCount => `${completeCount}/3`,
);

// Vocab / Radio : percent complete
export const getVocabProgressRatio = createSelector(
  [
    getVocabSessionsCompleteCount,
    getIsVocabComplete,
  ],
  (completeCount, isStatusComplete) => {
    if (isStatusComplete) {
      return 1;
    }
    return (completeCount / 3);
  },
);

export const getChallengeQuestProgressRatio = createSelector(
  [getChallengeQuestGamesCompleteCount],
  totalCount => totalCount / 3,
);

export const getChallengeQuestProgressText = createSelector(
  [getChallengeQuestGamesCompleteCount],
  totalCount => `${totalCount}/3`,
);

// Skill Game Progress / Battery – Number of games complete (not mastered) / total number of games available
export const getSkillGamesProgressText = createSelector(
  [
    getMasteredGamesCount,
    getTotalQuestGamesCount,
  ],
  (completeCount, totalCount) =>
    `${completeCount}/${totalCount}`
  ,
);

// Skill Game Progress / Battery – Number of games complete (not mastered) / total number of games available
export const getSkillGamesProgressRatio = createSelector(
  [
    getMasteredGamesCount,
    getTotalQuestGamesCount,
  ],
  (completeCount, totalCount) => (completeCount / totalCount),
);

// for workshop battery display only: Number of currently displayed games complete / total number of games displayed.
// makes an exception for cases when the battery marked as full from config or when the "real" progress ratio is 1
export const getBatteryProgressRatio = createSelector(
  [
    getGamesForDisplay,
    getSkillGamesProgressRatio,
    getCurrentStepConfig,
  ],
  (gamesInDisplay, progressRatio, { fullBattery = false } = {}) => {
    if (fullBattery || progressRatio >= 1) {
      return 1;
    }
    return gamesInDisplay.reduce((accu, current) =>
      (current.levels.length <= current.levelsMastered.length ? accu + 1 : accu), 0) / gamesInDisplay.length;
  },
);

export const getAreAllQuestActivitiesComplete = createSelector(
  [
    getSkillGamesProgressRatio,
    getCloseReadingProgressRatio,
    getEreaderProgressRatio,
    getIsVocabComplete,
  ],
  (batteryProgress, portalProgress, bookProgress, isVocabComplete) =>
    (batteryProgress === 1
        && portalProgress === 1
        && bookProgress === 1
        && isVocabComplete),
);

export const getIsVocabSessionComplete = createSelector(
  [
    getCurrentWorkshopQuest,
    (_, activityId) => activityId,
  ],
  ({ vocab: { sessions } }, activityId) => {
    const index = getIndexFromActivityId(activityId);
    const currentSession = sessions[index] || {};
    return currentSession.progress === VOCAB_PROGRESS.COMPLETE;
  },
);

export const getProgressData = createSelector(
  [
    getSkillGamesProgressText,
    getVocabProgressText,
    getCloseReadingProgressText,
    getEreaderProgressText,
  ],
  (batteryProgressText, radioProgressText, portalProgressText, bookProgressText) => [{
    title: 'Skill Games',
    progress: batteryProgressText,
    type: 'battery',
  },
  {
    title: 'Vocabulary',
    progress: radioProgressText,
    type: 'vocab',
  },
  {
    title: 'Close Reading',
    progress: portalProgressText,
    type: 'portal',
  },
  {
    title: 'Challenge Text',
    progress: bookProgressText,
    type: 'book',
  }],
);

export const getActivitiesProgressRation = createSelector(
  [getSkillGamesProgressRatio, getCloseReadingProgressRatio, getEreaderProgressRatio, getVocabProgressRatio],
  (batteryCompleteRatio, portalCompleteRatio, bookCompleteRatio, radioCompleteRatio) => ({
    batteryCompleteRatio,
    portalCompleteRatio,
    bookCompleteRatio,
    radioCompleteRatio,
  }),
);
